import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Chip,
  Menu,
  List,
  Divider,
  MenuItem,
  ListItem,
  Accordion,
  TextField,
  Typography,
  FormControl,
  ListItemText,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ArrowDropDown, ArrowDropUp, Search } from "@mui/icons-material";

import useAuth from "hooks/useAuth";
import SwitchRoleButton from "./SwitchRoleButton";
import LoadingModal from "components/common/loading/LoadingModal";
import { useSwitchRoleMutation } from "app/store/user/api/userApi";

import { filterNonUCHAccounts } from "utils/account.util";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { EVOKE_ROLES, USER_ROLE_MAPPER } from "static/constants";

const SwitchRoleMenu = () => {
  const { currentUser } = useSelector((state) => state.auth);

  const {
    userId,
    accounts,
    secondary_roles,
    role: currentRole,
    primary_role: primaryRole,
  } = currentUser;

  const filteredAccounts = filterNonUCHAccounts(accounts);
  const userAccount = filteredAccounts?.length === 1 && filteredAccounts?.[0];

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuItemIndex, setMenuItemIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const open = Boolean(anchorEl);

  const [switchRole, { isLoading }] = useSwitchRoleMutation();
  const { handleLogin } = useAuth({});

  const filteredRoles = useMemo(
    () =>
      secondary_roles?.filter(
        ({ account, roles }) =>
          account.toLowerCase().includes(searchTerm.toLowerCase()) ||
          roles.some((role) =>
            USER_ROLE_MAPPER[role]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
          )
      ),
    [searchTerm, secondary_roles]
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccordionToggle = (index) => {
    setMenuItemIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleSwitch = async (role, account, isPrimary = false) => {
    try {
      const result = await switchRole({
        role,
        userId,
        account,
        isPrimaryRole: isPrimary,
      }).unwrap();

      if (result) {
        await showSuccessToast(result?.message);
        handleLogin(result?.data, true);
        handleClose();
      }
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message;
      showErrorToast(errorMessage);
    }
  };

  return (
    <>
      {isLoading && (
        <LoadingModal open={isLoading} loadingText="Switching Role ..." />
      )}
      <div
        style={{ position: "relative", maxWidth: "300px", marginRight: "15px" }}
      >
        {(currentRole || userAccount) && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "-9px",
              left: 10,
              zIndex: "100",
              backgroundColor: "#fff",
              color: open ? " #94BA33" : "#757575",
            }}
          >
            Account & Role
          </Typography>
        )}
        <Box
          component="div"
          onClick={handleClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: "300px",
            height: "49px",
            padding: "8px 10px",
            border: open ? "2px solid #94BA33" : "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
            backgroundColor: "#fff",
            position: "relative",
            transition: "border 0.2s",
            marginRight: "15px",
          }}
        >
          <div className="d-flex justify-between align-items-center w-100">
            <Typography
              variant="body1"
              sx={{
                display: "-webkit-box",
                "line-clamp": "2 !important",
                "-webkit-line-clamp": "2 !important",
                "-webkit-box-orient": "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginRight: "8px",
                wordBreak: "break-word",
                lineHeight: "1.15 !important",
              }}
            >
              {EVOKE_ROLES.includes(currentRole)
                ? "Multiple Accounts"
                : userAccount || "Account & Role"}
            </Typography>

            <Chip
              label={USER_ROLE_MAPPER[currentRole]}
              variant="filled"
              color="secondary"
              size="small"
            />
          </div>
          {open ? <ArrowDropUp /> : <ArrowDropDown />}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            sx: { padding: 0 },
          }}
        >
          <MenuItem
            disableRipple
            sx={{
              minWidth: "300px",
              maxWidth: "300px",
              height: "100%",
              maxHeight: "700px",
              overflowY: "auto",
              width: "100%",
              backgroundColor: "white !important",
              padding: 0,
            }}
            className="d-flex flex-column w-100"
          >
            <FormControl fullWidth margin="dense" sx={{ padding: "8px 16px" }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search Account or Role"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: "#969696" }} />,
                }}
              />
            </FormControl>

            <Divider className="w-100" />

            <div
              className="d-flex flex-column w-100 g-1"
              style={{ padding: "12px 16px" }}
            >
              <h4
                style={{
                  fontSize: "16px",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                }}
              >
                Primary Role
              </h4>

              <List>
                <ListItem
                  disableGutters
                  secondaryAction={
                    <SwitchRoleButton
                      isActive={currentRole === primaryRole}
                      handleSwitch={() => {
                        if (!isLoading) {
                          handleSwitch(primaryRole, userAccount, true);
                        }
                      }}
                    />
                  }
                >
                  <ListItemText
                    primary={"Multiple Accounts"}
                    secondary={USER_ROLE_MAPPER[primaryRole]}
                    primaryTypographyProps={{ style: { marginBottom: 4 } }}
                  />
                </ListItem>
              </List>
            </div>

            <Divider className="w-100" />

            <div
              className="d-flex flex-column w-100 g-1"
              style={{ padding: "12px 0" }}
            >
              <h4
                style={{
                  fontSize: "16px",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  paddingLeft: "16px",
                }}
              >
                Secondary Roles
              </h4>

              <div className="d-flex flex-column w-100">
                {filteredRoles?.length > 0 ? (
                  filteredRoles.map(({ account, roles }, index) => {
                    const activeAccount = account === userAccount;
                    const expanded = activeAccount || menuItemIndex === index;

                    return (
                      <Accordion
                        key={index}
                        expanded={expanded}
                        onChange={() => handleAccordionToggle(index)}
                        sx={{
                          width: "100%",
                          padding: 0,
                          boxShadow: "none",
                          border: "0 !important",
                          backgroundColor: expanded ? "#F5F5F5" : "transparent",
                          margin: "0 !important",
                          "&:before": {
                            border: "0 !important",
                            background: "transparent !important",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ArrowDropDown />}
                          sx={{
                            border: "0 !important",
                            minHeight: "48px !important",
                            whiteSpace: "normal",
                            "& .MuiAccordionSummary-expandIconWrapper": {
                              flexShrink: 0,
                            },
                            ".Mui-expanded & .summary-text": {
                              fontSize: "16px",
                              lineHeight: "20px",
                              fontWeight: 500,
                            },
                            "& .summary-text": {
                              fontSize: "16px",
                              fontWeight: 400,
                              lineHeight: "24px !important",
                              display: "-webkit-box",
                              "-webkit-line-clamp": 2,
                              "-webkit-box-orient": "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "80%",
                            },
                          }}
                        >
                          <Typography className="summary-text">
                            {account}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            padding: 0,
                            paddingLeft: 2,
                            backgroundColor: "#FFFFFF",
                          }}
                        >
                          <List disablePadding>
                            {roles?.map((role, index) => {
                              const activeRole =
                                activeAccount && currentRole === role;
                              return (
                                <ListItem
                                  key={index}
                                  sx={{
                                    borderLeft: activeRole
                                      ? "4px solid #C5C5C5"
                                      : "3px solid #F5F5F5",
                                    padding: "8px",
                                    backgroundColor: activeRole
                                      ? "#F5F5F5"
                                      : "transparent",
                                    cursor: "default",
                                    "&:hover": { backgroundColor: "#F5F5F5" },
                                  }}
                                  secondaryAction={
                                    <SwitchRoleButton
                                      isActive={activeRole}
                                      handleSwitch={() => {
                                        if (!isLoading) {
                                          handleSwitch(role, account);
                                        }
                                      }}
                                    />
                                  }
                                >
                                  {USER_ROLE_MAPPER[role]}
                                </ListItem>
                              );
                            })}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                ) : (
                  <Typography sx={{ padding: "8px 16px", color: "#757575" }}>
                    No matching accounts found.
                  </Typography>
                )}
              </div>
            </div>
          </MenuItem>
        </Menu>
      </div>
    </>
  );
};

export default SwitchRoleMenu;
